/**
 * `/book-management/*` endpoints: create-with-inventory, the issue and return
 * workflows, bulk operations and workflow history.
 */

import apiBase, { BaseQueryParams, PaginatedResponse } from './api-base';
import type { Book, Author, Publisher, Genre } from './catalog-api';
import type { BookCopy, CopyLocation, Condition } from './inventory-api';
import type { Loan, LoanUserInfo, Fine } from './loans-api';

/** Types */

// Book Management specific interfaces
export interface BookWithAvailability extends Book {
  total_copies: number;
  available_copies: number;
  borrowed_copies: number;
  maintenance_copies: number;
  availability_status: 'available' | 'limited' | 'unavailable';
  next_available_date?: string;
  hold_queue_count?: number;
}

export interface BookCopyWithDetails extends BookCopy {
  book_info: {
    id: number;
    title: string;
    isbn: string;
    authors: Author[];
    publisher_info?: Publisher;
    genres: Genre[];
  };
  current_loan?: {
    id: number;
    borrower_name: string;
    due_date: string;
    is_overdue: boolean;
  };
  location_info?: CopyLocation | null;
  condition_info?: Condition;
}

export interface BookManagementError {
  type: 'validation' | 'business_logic' | 'network' | 'permission';
  code: string;
  message: string;
  details?: any;
  field?: string;
}

// Workflow Data Types
export interface CreateBookWithInventoryData {
  // Book metadata
  isbn: string;
  title: string;
  subtitle?: string;
  publication_date: string;
  description?: string;
  language: string;
  pages?: number;
  edition?: string;
  series?: string;
  series_number?: number;
  
  // Option 1: Send IDs (for future API version)
  author_ids?: number[];
  genre_ids?: number[];
  publisher_id?: number;
  
  // Option 2: Send names (current API format)
  authors?: string[];
  genres?: string[];
  publisher_name?: string;
  
  ddc_ids?: number[];

  cover_image?: File;
  cover_image_url?: string;  // Alternative: URL for backend to download image
  
  // Inventory data
  number_of_copies?: number;  // Backend format (generates copies automatically)
  condition?: string;         // Backend format (for auto-generated copies)
  
  // Alternative: detailed copies (frontend format)
  copies?: Array<{
    barcode: string;
    accession_number: string;
    acquisition_date: string;
    acquisition_cost?: string;
    acquisition_source?: string;
    location_id?: number;
    condition_id: number;
    edition_notes?: string;
    is_reference_only?: boolean;
    is_rare_book?: boolean;
    requires_approval?: boolean;
    internal_notes?: string;
  }>;
}

export interface CreateBookWithInventoryResponse {
  book: Book;
  copies: BookCopy[];
  message: string;
  created_copies_count: number;
}

export interface IssueBookWorkflowData {
  book_copy_id: number;
  borrower_id: number;
  due_date?: string; // Optional - will be calculated based on policy if not provided
  notes?: string;
  override_limits?: boolean; // For librarian override
}

export interface IssueBookWorkflowResponse {
  loan: Loan;
  book_copy: BookCopyWithDetails;
  borrower: LoanUserInfo;
  message: string;
  receipt_data?: {
    loan_id: number;
    book_title: string;
    due_date: string;
    borrower_name: string;
    issued_by: string;
    issue_date: string;
  };
}

export interface ReturnBookWorkflowData {
  loan_id: number;
  condition_assessment: {
    condition_id: number;
    damage_notes?: string;
    requires_maintenance?: boolean;
  };
  return_notes?: string;
  waive_fines?: boolean; // For librarian override
}

export interface ReturnBookWorkflowResponse {
  loan: Loan;
  book_copy: BookCopyWithDetails;
  fine?: Fine;
  message: string;
  receipt_data?: {
    loan_id: number;
    book_title: string;
    return_date: string;
    borrower_name: string;
    fine_amount?: string;
    processed_by: string;
  };
}

export interface BulkBookOperationsData {
  operation_type: 'update' | 'delete' | 'status_change' | 'location_change';
  book_ids?: number[]; // For book-level operations
  copy_ids?: number[]; // For copy-level operations
  
  // Update data (for bulk updates)
  update_data?: {
    // Book fields
    genre_ids?: number[];
    publisher_id?: number;
    language?: string;
    
    // Copy fields
    location_id?: number;
    condition_id?: number;
    status?: BookCopy['status'];
    internal_notes?: string;
  };
  
  // Operation metadata
  notes?: string;
  force_delete?: boolean; // For deleting books with active loans
}

export interface BulkOperationResult {
  operation_type: string;
  total_items: number;
  successful_items: number;
  failed_items: number;
  results: Array<{
    item_id: number;
    item_type: 'book' | 'copy';
    status: 'success' | 'error';
    message?: string;
    error?: string;
  }>;
  summary: {
    books_affected: number;
    copies_affected: number;
    execution_time_ms: number;
  };
}

// Search and Filter Types
export interface BookManagementSearchParams extends BaseQueryParams {
  // Basic search
  query?: string;
  book_id?: number;
  borrower_id?: number;
  loan_id?: number;
  copy_id?: number;
  barcode?: string;
  
  // Book filters
  author_name?: string;
  genre_name?: string;
  publisher_name?: string;
  language?: string;
  publication_year?: number;
  isbn?: string;
  
  // Availability filters
  availability_status?: 'available' | 'limited' | 'unavailable';
  has_available_copies?: boolean;
  min_available_copies?: number;
  
  // Copy filters
  location_id?: number;
  condition_id?: number;
  status?: BookCopy['status'];
  
  // Date filters
  acquisition_date_from?: string;
  acquisition_date_to?: string;
  last_borrowed_from?: string;
  last_borrowed_to?: string;
  
  // Advanced filters
  is_overdue?: boolean;
  requires_maintenance?: boolean;
  is_popular?: boolean; // High circulation rate
  is_underutilized?: boolean; // Low circulation rate
}

export interface BookManagementAnalytics {
  summary: {
    total_books: number;
    total_copies: number;
    available_copies: number;
    active_loans: number;
    overdue_loans: number;
    books_added_today: number;
    books_issued_today: number;
    books_returned_today: number;
  };
  
  // Extended fields for dashboard
  books_created_today?: number;
  books_created_this_week?: number;
  books_created_this_month?: number;
  books_issued_today?: number;
  books_returned_today?: number;
  workflows_completed_today?: number;
  maintenance_requests_pending?: number;
  inventory_audits_needed?: number;
  new_members_today?: number;
  new_members_this_week?: number;
  popular_books_low_stock?: number;
  average_processing_time?: number;
  
  circulation_trends: Array<{
    date: string;
    issues: number;
    returns: number;
    net_change: number;
  }>;
  
  popular_books: Array<{
    book: BookWithAvailability;
    circulation_count: number;
    current_demand: number;
  }>;
  
  collection_health: {
    condition_distribution: Array<{
      condition_name: string;
      count: number;
      percentage: number;
    }>;
    maintenance_required: number;
    missing_books: number;
    withdrawn_books: number;
  };
  
  inventory_insights: {
    underutilized_books: Array<{
      book: BookWithAvailability;
      days_since_last_borrowed: number;
      total_circulations: number;
    }>;
    high_demand_books: Array<{
      book: BookWithAvailability;
      hold_queue_count: number;
      availability_ratio: number;
    }>;
  };

  genre_popularity?: Array<{
    genre_id: number;
    genre_name: string;
    book_count: number;
    circulation_count: number;
    available_copies: number;
  }>;

  member_preferences?: Array<{
    genre_id: number;
    genre_name: string;
    member_count: number;
    total_loans: number;
    avg_loans_per_member: number;
    preference_score: number;
    retention_rate: number;
  }>;
}

// Query parameter types
export interface AnalyticsQueryParams {
  date_from?: string;
  date_to?: string;
  date_range?: 'today' | 'week' | 'month' | 'year' | 'custom';
  include_trends?: boolean;
  include_popular?: boolean;
  include_health?: boolean;
  include_insights?: boolean;
  include_activity?: boolean;
  include_genres?: boolean;
  include_member_preferences?: boolean;
  limit?: number;
}

// Today's Activity Types
export interface TodayBookActivity {
  booksAdded: Array<{
    id: number;
    title: string;
    author: string;
    addedAt: string;
    copiesCreated: number;
  }>;
  booksIssued: Array<{
    id: number;
    bookTitle: string;
    borrowerName: string;
    issuedAt: string;
    dueDate: string;
  }>;
  booksReturned: Array<{
    id: number;
    bookTitle: string;
    borrowerName: string;
    returnedAt: string;
    condition: string;
    fineAmount?: number;
  }>;
  bulkOperations: Array<{
    id: string;
    type: 'update' | 'delete' | 'status_change';
    itemsProcessed: number;
    completedAt: string;
    status: 'completed' | 'partial' | 'failed';
  }>;
}

/**
 * Book Management API endpoints
 */
export const bookManagementApi = {
  /**
   * Create book with automatic inventory generation
   * @param data - Book and inventory creation data
   * @returns Promise with created book and copies
   */
  createBookWithInventory: async (data: CreateBookWithInventoryData): Promise<CreateBookWithInventoryResponse> => {
    const hasCoverImageFile = data.cover_image instanceof File;

    if (hasCoverImageFile) {
      // Multipart request: needed to upload the file.
      // DRF ListField can't parse a JSON-encoded string for `copies` from multipart,
      // so we send copies as repeated JSON-object fields. Other arrays (author_ids,
      // genre_ids, ddc_ids) are sent as repeated scalar fields.
      const formData = new FormData();

      Object.entries(data).forEach(([key, value]) => {
        if (key === 'copies') return; // handled below
        if (key === 'cover_image' && value instanceof File) {
          formData.append(key, value);
        } else if (Array.isArray(value)) {
          // Repeated scalar fields that DRF ListField can parse from multipart
          (value as unknown[]).forEach(item => formData.append(key, String(item)));
        } else if (value !== undefined && value !== null) {
          formData.append(key, String(value));
        }
      });

      // Send each copy as a JSON-encoded entry; the backend reads copies via
      // request.data which returns a list when the same key is repeated.
      if (data.copies && data.copies.length > 0) {
        data.copies.forEach(copy => formData.append('copies', JSON.stringify(copy)));
      }

      const response = await apiBase.post('/book-management/create-with-inventory/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    }

    // No file upload: send a plain JSON body so DRF can correctly deserialize
    // ListField fields (copies, author_ids, genre_ids, ddc_ids).
    const jsonBody: Record<string, unknown> = {};
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        jsonBody[key] = value;
      }
    });

    const response = await apiBase.post('/book-management/create-with-inventory/', jsonBody);
    return response.data;
  },

  /**
   * Issue book workflow with validation and loan creation
   * @param data - Issue workflow data
   * @returns Promise with loan and receipt data
   */
  issueBookWorkflow: async (data: IssueBookWorkflowData): Promise<IssueBookWorkflowResponse> => {
    const response = await apiBase.post('/book-management/issue-workflow/', data);
    return response.data;
  },

  /**
   * Return book workflow with condition assessment and fine calculation
   * @param data - Return workflow data
   * @returns Promise with updated loan and fine data
   */
  returnBookWorkflow: async (data: ReturnBookWorkflowData): Promise<ReturnBookWorkflowResponse> => {
    const response = await apiBase.post('/book-management/return-workflow/', data);
    return response.data;
  },

  /**
   * Bulk operations on books and copies
   * @param data - Bulk operation data
   * @returns Promise with operation results
   */
  bulkOperations: async (data: BulkBookOperationsData): Promise<BulkOperationResult> => {
    // Transform frontend data format to backend format
    const backendPayload = {
      operation: data.operation_type, // Backend expects 'operation', not 'operation_type'
      book_ids: data.book_ids || [],
      copy_ids: data.copy_ids || [],
      update_data: data.update_data || {},
      new_status: data.update_data?.status || undefined, // Backend expects 'new_status' for status_change
      force_delete: data.force_delete || false,
    };

    const response = await apiBase.post('/book-management/bulk-operations/', backendPayload);
    
    // Transform backend response to frontend format
    const backendData = response.data;
    const transformedResult: BulkOperationResult = {
      operation_type: data.operation_type,
      total_items: backendData.total || 0,
      successful_items: backendData.successful || 0,
      failed_items: backendData.failed || 0,
      results: (backendData.errors || []).map((error: any) => ({
        item_id: error.book_id || error.copy_id || 0,
        item_type: error.book_id ? 'book' : 'copy',
        status: 'error',
        error: error.error,
        message: error.error
      })),
      summary: {
        books_affected: backendData.total || 0,
        copies_affected: 0,
        execution_time_ms: 0
      }
    };

    return transformedResult;
  },

  /**
   * Books with their availability annotations
   * @param params - Search and filter parameters
   * @returns Promise with paginated book list with availability
   */
  getBooksWithAvailability: async (params?: BookManagementSearchParams): Promise<PaginatedResponse<BookWithAvailability>> => {
    const response = await apiBase.get('/book-management/books-with-availability/', { params });
    return response.data;
  },

  /**
   * Get book copies with detailed information
   * @param params - Search and filter parameters
   * @returns Promise with paginated copy list with details
   */
  getCopiesWithDetails: async (params?: BookManagementSearchParams): Promise<PaginatedResponse<BookCopyWithDetails>> => {
    const response = await apiBase.get('/book-management/copies-with-details/', { params });
    return response.data;
  },

  /**
   * Book management analytics
   * @param params - Analytics parameters
   * @returns Promise with analytics data
   */
  getAnalytics: async (params?: AnalyticsQueryParams): Promise<BookManagementAnalytics> => {
    const response = await apiBase.get('/book-management/analytics/', { params });
    return response.data;
  },

  /**
   * Get today's book management activity
   * @returns Promise with today's activity data
   */
  getTodayActivity: async (): Promise<TodayBookActivity> => {
    const response = await apiBase.get('/book-management/today-activity/');
    return response.data;
  },

  /**
   * Check book issue eligibility
   * @param bookCopyId - Book copy ID
   * @param borrowerId - Borrower user ID
   * @returns Promise with eligibility check result
   */
  checkIssueEligibility: async (bookCopyId: number, borrowerId: number): Promise<{
    eligible: boolean;
    reason?: string;
    warnings?: string[];
    loan_policy: {
      max_books: number;
      loan_period_days: number;
      max_renewals: number;
    };
    borrower_status: {
      current_loans: number;
      overdue_loans: number;
      outstanding_fines: string;
    };
  }> => {
    const response = await apiBase.get('/book-management/check-issue-eligibility/', {
      params: { book_copy_id: bookCopyId, borrower_id: borrowerId }
    });
    return response.data;
  },

  /**
   * Get book recommendations for librarians
   * @param params - Recommendation parameters
   * @returns Promise with recommended books
   */
  getRecommendations: async (params?: {
    type?: 'acquisition' | 'popular' | 'underutilized';
    limit?: number;
    genre_id?: number;
  }): Promise<{
    acquisition_recommendations: BookWithAvailability[];
    popular_books: BookWithAvailability[];
    underutilized_books: BookWithAvailability[];
  }> => {
    const response = await apiBase.get('/book-management/recommendations/', { params });
    return response.data;
  },

  /**
   * Advanced search with multiple criteria
   * @param searchData - Advanced search criteria
   * @returns Promise with search results
   */
  advancedSearch: async (searchData: {
    query?: string;
    filters: BookManagementSearchParams;
    sort_by?: 'title' | 'author' | 'publication_date' | 'popularity' | 'availability';
    sort_order?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<BookWithAvailability>> => {
    // Note: Using catalog API for advanced book search
    const response = await apiBase.get('/catalog/books/advanced-search/', { params: searchData });
    return response.data;
  },

  /**
   * Get workflow history for audit purposes
   * @param params - History query parameters
   * @returns Promise with workflow history
   */
  getWorkflowHistory: async (params?: {
    workflow_type?: 'create' | 'issue' | 'return' | 'bulk' | 'quick_issue' | 'quick_return';
    user_id?: number;
    status?: 'success' | 'error' | 'partial';
    date_from?: string;
    date_to?: string;
    book_id?: number;
    member_id?: number;
    loan_id?: number;
    page?: number;
    page_size?: number;
    ordering?: string;
  }): Promise<PaginatedResponse<{
    id: number;
    workflow_type: string;
    user: {
      id: number;
      username: string;
      full_name: string;
      email: string;
    } | null;
    input_data: any;
    result_data: any;
    status: 'success' | 'error' | 'partial';
    error_message?: string | null;
    error_details?: any;
    execution_time_ms?: number | null;
    execution_time_seconds?: number | null;
    created_at: string;
    ip_address?: string | null;
    book_id?: number | null;
    member_id?: number | null;
    loan_id?: number | null;
  }> & {
    statistics: {
      total_workflows: number;
      success_count: number;
      error_count: number;
      partial_count: number;
      avg_execution_time_ms: number;
      success_rate: number;
    };
  }> => {
    const response = await apiBase.get('/book-management/workflow-history/', { params });
    return response.data;
  },
};

export default bookManagementApi;